import axios from "axios";
import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {
  type Configuration,
  type ConfigurationError,
  type ConfigurationRequestDto,
  type ConfigurationResponseDto,
  type ConfigurationValidationErrors,
  type UserGroup,
  type UserType
} from "../types";
import {edit, getConfiguration, getUserGroups, getUserTypes, uploadConfiguration} from "./editApi";
import {
  mapConfigurationToDto,
  mapUserGroupsDtoToUserGroups,
  mapUserTypesDtoToUserTypes
} from "../mappers";
import {configurationLoadErrorText, editFailedText, editUserUnauthorizedText, statusToErrorText} from "./constants";
import {isSuccess} from "../../../infrastructure/http-status";
import {clearValidationAction} from "../common/clearValidationAction";
import {Status} from "../../../infrastructure/status";
import {type ApiResponse} from "../../../infrastructure/types";
import {type WithState} from "../../../store/utils";

export interface EditState {
  editStatus: Status;
  userTypes: UserType[];
  userTypesStatus: Status;
  userGroups: UserGroup[];
  userGroupsStatus: Status;
  configuration?: Configuration;
  configurationStatus: Status;
  validationErrors: ConfigurationValidationErrors;
}

const initialState: EditState = {
  editStatus: Status.IDLE,
  userGroups: [],
  userGroupsStatus: Status.LOADING,
  userTypes: [],
  userTypesStatus: Status.LOADING,
  configuration: undefined,
  configurationStatus: Status.LOADING,
  validationErrors: {}
};

export interface EditConfiguration {
  configuration: ConfigurationRequestDto;
  id: number;
}

export interface UploadConfiguration {
  data: FormData;
  id: number;
}

export const editAsync = createAsyncThunk<
  ConfigurationResponseDto,
  Configuration,
  {rejectValue: ConfigurationError}
>("edit/edit", async (configuration, {fulfillWithValue, rejectWithValue}) => {
  try {
    const response = await edit(mapConfigurationToDto(configuration), configuration.id);
    const editResult = response.data;

    const file = configuration.file[0];

    if (file) {
      const formData = new FormData();
      formData.append("file", file);

      await uploadConfiguration(formData, configuration.id);
    }

    return fulfillWithValue(editResult);
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      const response = err?.response?.data as ApiResponse<ConfigurationResponseDto>;
      return rejectWithValue({
        message: response?.messages?.userMessages?.join("\r\n") || err?.response?.status == 401 ? editUserUnauthorizedText : editFailedText,
        validationErrors: response?.messages?.validationErrors ?? {}
      });
    }
  }
});

export const getConfigurationAsync = createAsyncThunk(
  "edit/get",
  async (id: number, {fulfillWithValue, rejectWithValue}) => {
    try {
      const result = await getConfiguration(id);

      if (isSuccess(result.status)) {
        return fulfillWithValue(result?.data?.data);
      }

      throw rejectWithValue({
        message: statusToErrorText[result.status] ?? configurationLoadErrorText
      });
    } catch (err) {
      throw rejectWithValue({
        message: statusToErrorText[err?.response?.status] ?? configurationLoadErrorText
      });
    }
  }
);

export const getUserTypesAsync = createAsyncThunk(
  "edit/userTypes",
  async () => (await getUserTypes())?.data?.data
);

export const getUserGroupsAsync = createAsyncThunk(
  "edit/userGroups",
  async () => (await getUserGroups())?.data?.data
);

export const editSlice = createSlice({
  name: "edit",
  initialState,
  reducers: {
    clearValidation: clearValidationAction,
    clearConfiguration: state => {
      state.configuration = undefined;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(editAsync.pending, state => {
        state.editStatus = Status.LOADING;
        state.validationErrors = {};
      })
      .addCase(editAsync.fulfilled, state => {
        state.editStatus = Status.IDLE;
      })
      .addCase(editAsync.rejected, (state, action) => {
        state.editStatus = Status.FAILED;
        state.validationErrors = action.payload.validationErrors;
      });

    builder
      .addCase(getUserGroupsAsync.pending, state => {
        state.userGroupsStatus = Status.LOADING;
      })
      .addCase(getUserGroupsAsync.fulfilled, (state, action) => {
        state.userGroups = mapUserGroupsDtoToUserGroups(action.payload);
        state.userGroupsStatus = Status.IDLE;
      })
      .addCase(getUserGroupsAsync.rejected, state => {
        state.userGroupsStatus = Status.FAILED;
      });

    builder
      .addCase(getUserTypesAsync.pending, state => {
        state.userTypesStatus = Status.LOADING;
      })
      .addCase(getUserTypesAsync.fulfilled, (state, action) => {
        state.userTypes = mapUserTypesDtoToUserTypes(action.payload);
        state.userTypesStatus = Status.IDLE;
      })
      .addCase(getUserTypesAsync.rejected, state => {
        state.userTypesStatus = Status.FAILED;
      });

    builder
      .addCase(getConfigurationAsync.pending, state => {
        state.configurationStatus = Status.LOADING;
      })
      .addCase(getConfigurationAsync.fulfilled, (state, action) => {
        state.configuration = {...action.payload, file: undefined};
        state.configurationStatus = Status.IDLE;
      })
      .addCase(getConfigurationAsync.rejected, state => {
        state.configurationStatus = Status.FAILED;
      });
  }
});

export const selectEditStatus = (state: WithState<EditState, "edit">) => state.edit.editStatus;
export const selectUserGroups = (state: WithState<EditState, "edit">) => state.edit.userGroups;
export const selectUserGroupsStatus = (state: WithState<EditState, "edit">) =>
  state.edit.userGroupsStatus;
export const selectUserTypes = (state: WithState<EditState, "edit">) => state.edit.userTypes;
export const selectUserTypesStatus = (state: WithState<EditState, "edit">) =>
  state.edit.userTypesStatus;
export const selectConfiguration = (state: WithState<EditState, "edit">) =>
  state.edit.configuration;
export const selectConfigurationStatus = (state: WithState<EditState, "edit">) =>
  state.edit.configurationStatus;
export const selectEditValidationErrors = (state: WithState<EditState, "edit">) =>
  state.edit.validationErrors;

export const {clearValidation, clearConfiguration} = editSlice.actions;

export const editReducer = editSlice.reducer;
